import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Brand } from "@/components/Brand";
import {
  LayoutDashboard,
  Building2,
  Users,
  CalendarCheck,
  ReceiptText,
  Wallet,
  CalendarDays,
  Calculator,
  Megaphone,
  FileText,
  LifeBuoy,
  Receipt,
  LogOut,
  ChevronRight,
} from "lucide-react";

function Item({ to, icon: Icon, label, testid }) {
  return (
    <NavLink
      to={to}
      end
      data-testid={testid}
      className={({ isActive }) =>
        `group flex items-center gap-3 px-3 py-2.5 text-sm border-l-2 transition-colors duration-150 ${
          isActive
            ? "border-l-[#C9A961] bg-white text-neutral-950 font-semibold"
            : "border-l-transparent text-neutral-600 hover:text-neutral-950 hover:bg-white/60"
        }`
      }
    >
      <Icon className={`w-4 h-4 ${label === "Calculator" ? "text-[#8C6F1E]" : ""}`} strokeWidth={1.75} />
      <span className="font-mono tracking-tight text-[13px]">{label}</span>
    </NavLink>
  );
}

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const nav = [];
  if (user.role === "super_admin") {
    nav.push({ to: "/app", label: "Overview", icon: LayoutDashboard, id: "nav-overview" });
    nav.push({ to: "/app/companies", label: "Clients", icon: Building2, id: "nav-companies" });
    nav.push({ to: "/app/calculator", label: "Calculator", icon: Calculator, id: "nav-calculator" });
  } else if (user.role === "company_admin") {
    nav.push({ to: "/app", label: "Overview", icon: LayoutDashboard, id: "nav-overview" });
    nav.push({ to: "/app/employees", label: "Employees", icon: Users, id: "nav-employees" });
    nav.push({ to: "/app/attendance", label: "Attendance", icon: CalendarCheck, id: "nav-attendance" });
    nav.push({ to: "/app/leaves", label: "Leaves", icon: CalendarDays, id: "nav-leaves" });
    nav.push({ to: "/app/expenses", label: "Expenses", icon: Receipt, id: "nav-expenses" });
    nav.push({ to: "/app/payroll", label: "Payroll", icon: Wallet, id: "nav-payroll" });
    nav.push({ to: "/app/payslips", label: "Payslips", icon: ReceiptText, id: "nav-payslips" });
    nav.push({ to: "/app/helpdesk", label: "Helpdesk", icon: LifeBuoy, id: "nav-helpdesk" });
    nav.push({ to: "/app/announcements", label: "Announcements", icon: Megaphone, id: "nav-announcements" });
    nav.push({ to: "/app/documents", label: "Documents", icon: FileText, id: "nav-documents" });
    nav.push({ to: "/app/calculator", label: "Calculator", icon: Calculator, id: "nav-calculator" });
  } else {
    nav.push({ to: "/app", label: "My Day", icon: LayoutDashboard, id: "nav-overview" });
    nav.push({ to: "/app/attendance", label: "Attendance", icon: CalendarCheck, id: "nav-attendance" });
    nav.push({ to: "/app/leaves", label: "My Leaves", icon: CalendarDays, id: "nav-leaves" });
    nav.push({ to: "/app/expenses", label: "Expenses", icon: Receipt, id: "nav-expenses" });
    nav.push({ to: "/app/payslips", label: "Payslips", icon: ReceiptText, id: "nav-payslips" });
    nav.push({ to: "/app/helpdesk", label: "Helpdesk", icon: LifeBuoy, id: "nav-helpdesk" });
    nav.push({ to: "/app/announcements", label: "Announcements", icon: Megaphone, id: "nav-announcements" });
    nav.push({ to: "/app/documents", label: "Documents", icon: FileText, id: "nav-documents" });
  }

  const roleLabel = user.role === "super_admin" ? "SERVICE PROVIDER" : user.role === "company_admin" ? "HR ADMIN" : "EMPLOYEE";

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-[#FAF7EF] text-neutral-950 flex">
      {/* Sidebar */}
      <aside className="w-64 border-r border-[#E8DFC5] bg-white flex flex-col" data-testid="sidebar">
        <div className="p-5 border-b border-[#E8DFC5]">
          <Brand size="md" variant="light" asLink />
          <div className="mt-3 text-[9px] font-mono uppercase tracking-[0.28em] text-[#8C6F1E]">
            A Harguaon Group Service
          </div>
        </div>

        <div className="px-3 py-2 border-b border-[#E8DFC5] bg-[#FAF7EF]">
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-neutral-500">Signed in as</div>
          <div className="mt-1 flex items-center gap-2">
            <div className="w-8 h-8 bg-neutral-950 text-[#D4B87D] flex items-center justify-center font-serif font-bold text-sm border border-[#C9A961]">
              {user.name?.[0]?.toUpperCase() || "U"}
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold truncate">{user.name}</div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#8C6F1E] truncate">{roleLabel}</div>
            </div>
          </div>
        </div>

        <nav className="flex-1 py-3 flex flex-col gap-0.5" data-testid="sidebar-nav">
          {nav.map((n) => (
            <Item key={n.to} to={n.to} icon={n.icon} label={n.label} testid={n.id} />
          ))}
        </nav>

        <button
          onClick={handleLogout}
          data-testid="btn-logout"
          className="m-3 flex items-center justify-between gap-2 px-3 py-2.5 border border-[#C9A961] hover:bg-neutral-950 hover:text-[#D4B87D] transition-colors duration-150"
        >
          <span className="flex items-center gap-2 font-mono text-[13px]">
            <LogOut className="w-4 h-4" strokeWidth={1.75} />
            Sign out
          </span>
          <ChevronRight className="w-4 h-4" />
        </button>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0">
        <Outlet />
      </main>
    </div>
  );
}
